import { useState, useRef, useEffect } from 'react';
import { Send, Loader2, MessageCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { theme } from '../../styles/theme';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ConnieAvatar } from '../../components/ConnieAvatar';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

interface ChatPanelProps {
  messages: ChatMessage[];
  loading: boolean;
  onSend: (message: string) => void;
}

export function ChatPanel({ messages, loading, onSend }: ChatPanelProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const mobile = useIsMobile();

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const stripMarkdown = (text: string) => text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^#+\s/gm, '')
    .replace(/^- /gm, '  ')
    .replace(/```[\s\S]*?```/g, '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || (loading && messages[messages.length - 1]?.role !== 'assistant')) return;
    setInput('');
    onSend(trimmed);
  };

  return (
    <div style={{ position: 'fixed', bottom: mobile ? 20 : 32, right: mobile ? 16 : 24, zIndex: 1000 }}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'absolute', bottom: 64, right: 0,
              width: mobile ? 'calc(100vw - 32px)' : 420,
              maxHeight: mobile ? '70vh' : '60vh',
              background: theme.colors.white,
              borderRadius: theme.borderRadius['2xl'],
              border: `1px solid ${theme.colors.borderLight}`,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.08)',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              padding: `${theme.spacing.md} ${theme.spacing.lg}`,
              borderBottom: `1px solid ${theme.colors.borderLight}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: theme.colors.navy,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <ConnieAvatar size={28} />
                <div>
                  <div style={{ color: theme.colors.white, fontSize: theme.fontSize.sm, fontWeight: theme.fontWeight.bold }}>Connie</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: theme.fontSize.xs }}>Refine your itinerary</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: 4 }}>
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div style={{
              flex: messages.length ? 1 : undefined, overflowY: 'auto',
              padding: theme.spacing.md,
              display: 'flex', flexDirection: 'column', gap: theme.spacing.md,
            }}>
              {messages.length === 0 && !loading && (
                <div style={{ textAlign: 'center', color: theme.colors.muted, fontSize: theme.fontSize.sm, padding: `${theme.spacing.lg} ${theme.spacing.md}`, fontWeight: theme.fontWeight.light }}>
                  Ask Connie to change activities, swap hotels, or adjust your trip.
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} style={{ alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                  <div style={{
                    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: msg.role === 'user' ? theme.colors.navy : theme.colors.tealBg,
                    color: msg.role === 'user' ? theme.colors.white : theme.colors.navy,
                    fontSize: theme.fontSize.sm,
                    lineHeight: 1.6,
                    fontWeight: theme.fontWeight.light,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {msg.role === 'assistant' ? stripMarkdown(msg.text) : msg.text}
                  </div>
                </div>
              ))}
              {loading && messages[messages.length - 1]?.role !== 'assistant' && (
                <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: theme.spacing.sm, padding: `${theme.spacing.md} ${theme.spacing.lg}`, background: theme.colors.tealBg, borderRadius: '16px 16px 16px 4px' }}>
                  <Loader2 size={14} color={theme.colors.teal} style={{ animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: theme.fontSize.sm, color: theme.colors.muted }}>Thinking...</span>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} style={{
              display: 'flex', alignItems: 'center', gap: theme.spacing.md,
              padding: `${theme.spacing.md} ${theme.spacing.lg}`,
              borderTop: `1px solid ${theme.colors.borderLight}`,
            }}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                disabled={loading && messages[messages.length - 1]?.role !== 'assistant'}
                style={{
                  flex: 1, border: 'none', outline: 'none',
                  fontSize: theme.fontSize.sm,
                  fontWeight: theme.fontWeight.light,
                  color: theme.colors.navy,
                  background: 'transparent',
                  padding: `${theme.spacing.sm} 0`,
                }}
              />
              <button
                type="submit"
                disabled={!input.trim() || (loading && messages[messages.length - 1]?.role !== 'assistant')}
                style={{
                  width: 36, height: 36, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', cursor: 'pointer',
                  background: input.trim() ? theme.colors.teal : theme.colors.borderLight,
                  color: input.trim() ? theme.colors.white : theme.colors.muted,
                  transition: 'all 0.2s ease', flexShrink: 0,
                }}
              >
                {loading && messages[messages.length - 1]?.role !== 'assistant' ? <Loader2 size={14} /> : <Send size={14} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB button */}
      <motion.button
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen(o => !o)}
        style={{
          width: 56, height: 56, borderRadius: '50%',
          background: open ? theme.colors.muted : theme.colors.navy,
          color: theme.colors.white,
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
          transition: 'background 0.2s ease',
        }}
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </motion.button>
    </div>
  );
}
